import { createApi } from '@reduxjs/toolkit/query/react';

import { SortOrder } from 'utils/enums';
import { baseQueryWithReauth } from 'utils/services';

import {
  AcceptFillConsentReqProps,
  AcceptFillConsentResProps,
  CancelRefillReqProps,
  CancelRefillResProps,
  ChangeRefillModeReqProps,
  ChangeRefillModeResProps,
  CheckPrescriptionEligibilityReqProps,
  GetAdminPrescriptionsReqProps,
  GetAdminPrescriptionsResProps,
  GetMedicalRecordsDataProps,
  GetMedicalRecordsReqProps,
  GetMedicalRecordsResProps,
  GetMpMagicLinkReqProps,
  GetMpMagicLinkResProps,
  GetPrescriptionReqProps,
  GetPrescriptionResProps,
  GetPrescriptionsReqProps,
  GetPrescriptionsResProps,
  PauseRefillReqProps,
  PauseRefillResProps,
  RequestRefillReqProps,
  RequestRefillResProps,
  RequestRenewalReqProps,
  RequestRenewalResProps,
  ResumeRefillReqProps,
  ResumeRefillResProps
} from './patientChart.types';

export const patientChartApi = createApi({
  baseQuery: baseQueryWithReauth,
  endpoints: (build) => ({
    acceptFillConsent: build.mutation<AcceptFillConsentResProps, AcceptFillConsentReqProps>({
      query: (body) => ({
        body,
        method: 'POST',
        url: '/prescriptions/consent/accept'
      })
    }),
    cancelRefill: build.mutation<CancelRefillResProps, CancelRefillReqProps>({
      query: (body) => ({
        body,
        method: 'POST',
        url: '/prescriptions/refill/cancel'
      })
    }),
    changeRefillToAutomatic: build.mutation<ChangeRefillModeResProps, ChangeRefillModeReqProps>({
      query: (body) => ({
        body,
        method: 'POST',
        url: '/prescriptions/refill/change-automatic'
      })
    }),
    changeRefillToManual: build.mutation<ChangeRefillModeResProps, ChangeRefillModeReqProps>({
      query: (body) => ({
        body,
        method: 'POST',
        url: '/prescriptions/refill/change-manual'
      })
    }),
    checkPrescriptionEligibility: build.mutation<unknown, CheckPrescriptionEligibilityReqProps>({
      query: (body) => ({
        body,
        method: 'POST',
        url: '/prescriptions/renewal/eligibility'
      })
    }),
    getAdminPrescriptions: build.query<
      GetAdminPrescriptionsResProps,
      GetAdminPrescriptionsReqProps
    >({
      keepUnusedDataFor: 0,
      query: (patientId) => `patients/${patientId}/prescriptions`
    }),
    getMedicalRecords: build.query<GetMedicalRecordsDataProps, GetMedicalRecordsReqProps>({
      keepUnusedDataFor: 0,
      query: (patientId) => `patients/${patientId}/my-chart`,
      transformResponse: (response: GetMedicalRecordsResProps) => response.data
    }),
    getMpMagicLink: build.query<GetMpMagicLinkResProps, GetMpMagicLinkReqProps>({
      query: (patientId) => `/patients/${patientId}/mp-magic-link`
    }),
    getPrescription: build.query<GetPrescriptionResProps, GetPrescriptionReqProps>({
      keepUnusedDataFor: 0,
      query: ({ patientId, prescriptionId }) =>
        `patients/${patientId}/prescriptions/${prescriptionId}`
    }),
    getPrescriptions: build.query<GetPrescriptionsResProps, GetPrescriptionsReqProps | void>({
      keepUnusedDataFor: 0,
      query: (params) => ({
        params: { sortField: 'signedDate', sortOrder: SortOrder.Desc, ...params },
        url: '/patient-chart/prescriptions'
      })
    }),
    pauseRefillWorkflow: build.mutation<PauseRefillResProps, PauseRefillReqProps>({
      query: (body) => ({
        body,
        method: 'POST',
        url: '/prescriptions/refill/pause'
      })
    }),
    requestRefill: build.mutation<RequestRefillResProps, RequestRefillReqProps>({
      query: (body) => ({
        body,
        method: 'POST',
        url: '/prescriptions/refill/request'
      })
    }),
    requestRenewal: build.mutation<RequestRenewalResProps, RequestRenewalReqProps>({
      query: (body) => ({
        body,
        method: 'POST',
        url: '/prescriptions/renewal/request'
      })
    }),
    resumeRefillWorkflow: build.mutation<ResumeRefillResProps, ResumeRefillReqProps>({
      query: (body) => ({
        body,
        method: 'POST',
        url: '/prescriptions/refill/resume'
      })
    })
  }),
  reducerPath: 'patientChartApi',
  tagTypes: ['patientChart']
});

export const {
  useAcceptFillConsentMutation,
  useCancelRefillMutation,
  useChangeRefillToAutomaticMutation,
  useChangeRefillToManualMutation,
  useCheckPrescriptionEligibilityMutation,
  useGetAdminPrescriptionsQuery,
  useGetMedicalRecordsQuery,
  useGetPrescriptionQuery,
  useGetPrescriptionsQuery,
  useLazyGetMpMagicLinkQuery,
  useLazyGetPrescriptionsQuery,
  usePauseRefillWorkflowMutation,
  useRequestRefillMutation,
  useRequestRenewalMutation,
  useResumeRefillWorkflowMutation
} = patientChartApi;
